
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InsightCategory {
    id: string;
    userId: string;
    name: string;
    emoji: string;
    color: string;
    createdAt: string;
    updatedAt: string;
}

export interface InsightCollection {
    id: string;
    userId: string;
    name: string;
    description: string | null;
    color: string;
    createdAt: string;
    updatedAt: string;
}

export interface InsightTag {
    id: string;
    userId: string;
    name: string;
    color: string;
    createdAt: string;
}

export interface Insight {
    id: string;
    userId: string;
    conversationId: string | null;
    messageId: string | null;
    personaId: string | null;
    contextId: string | null;
    collectionId: string | null;
    tags: InsightTag[];
    sourceType: 'full_message' | 'selection' | 'ai_suggested' | 'full_conversation';
    originalContent: string;
    highlightedExcerpt: string | null;
    aiTitle: string;
    aiSummary: string;
    aiCategory: string;
    aiSentiment: string;
    userNote: string | null;
    isPinned: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface InsightCreateInput {
    conversationId: string;
    messageId?: string;
    personaId: string;
    contextId?: string;
    collectionId?: string;
    tagIds?: string[];
    sourceType: 'full_message' | 'selection' | 'ai_suggested' | 'full_conversation';
    originalContent: string;
    highlightedExcerpt?: string;
    aiTitle: string;
    aiSummary: string;
    aiCategory: string;
    aiSentiment: string;
    userNote?: string;
}

// ---------------------------------------------------------------------------
// Row mappers (snake_case → camelCase)
// ---------------------------------------------------------------------------

type TagRowType = { tag: Database['public']['Tables']['insight_tags']['Row'] };

type InsightRowWithTags = Database['public']['Tables']['insights']['Row'] & {
    tags?: TagRowType[] | null;
};

const mapRow = (row: InsightRowWithTags): Insight => ({
    id: row.id,
    userId: row.user_id,
    conversationId: row.conversation_id,
    messageId: row.message_id,
    personaId: row.persona_id,
    contextId: row.context_id,
    collectionId: row.collection_id,
    tags: row.tags ? row.tags.map((t) => ({
        id: t.tag.id,
        userId: t.tag.user_id,
        name: t.tag.name,
        color: t.tag.color || '',
        createdAt: t.tag.created_at,
    })) : [],
    sourceType: row.source_type as Insight['sourceType'],
    originalContent: row.original_content,
    highlightedExcerpt: row.highlighted_excerpt,
    aiTitle: row.ai_title,
    aiSummary: row.ai_summary,
    aiCategory: row.ai_category,
    aiSentiment: row.ai_sentiment || '',
    userNote: row.user_note,
    isPinned: row.is_pinned ?? false,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

const mapCollectionRow = (row: Database['public']['Tables']['insight_collections']['Row']): InsightCollection => ({
    id: row.id,
    userId: row.user_id,
    name: row.name,
    description: row.description,
    color: row.color || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

const mapTagRow = (row: Database['public']['Tables']['insight_tags']['Row']): InsightTag => ({
    id: row.id,
    userId: row.user_id,
    name: row.name,
    color: row.color || '',
    createdAt: row.created_at,
});

const mapCategoryRow = (row: Database['public']['Tables']['insight_categories']['Row']): InsightCategory => ({
    id: row.id,
    userId: row.user_id,
    name: row.name,
    emoji: row.emoji,
    color: row.color,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
});

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const insightService = {
    async fetchAll(offset: number = 0, limit: number = 50): Promise<Insight[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase
            .from('insights')
            .select(`*, tags:insight_tag_links(tag:insight_tags(*))`)
            .eq('user_id', user.id)
            .order('is_pinned', { ascending: false })
            .order('created_at', { ascending: false })
            .range(offset, offset + limit - 1);

        if (error) throw error;
        return (data || []).map(mapRow);
    },

    async create(input: InsightCreateInput): Promise<Insight> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase.from('insights').insert({
            user_id: user.id,
            conversation_id: input.conversationId,
            message_id: input.messageId || null,
            persona_id: input.personaId,
            context_id: input.contextId || null,
            collection_id: input.collectionId || null,
            source_type: input.sourceType,
            original_content: input.originalContent,
            highlighted_excerpt: input.highlightedExcerpt || null,
            ai_title: input.aiTitle,
            ai_summary: input.aiSummary,
            ai_category: input.aiCategory,
            ai_sentiment: input.aiSentiment,
            user_note: input.userNote || null,
        }).select(`*, tags:insight_tag_links(tag:insight_tags(*))`).single();

        if (error) throw error;
        const newInsight = mapRow(data);

        if (input.tagIds && input.tagIds.length > 0) {
            await insightService.updateTags(newInsight.id, input.tagIds);
            const { data: updatedData } = await supabase
                .from('insights')
                .select(`*, tags:insight_tag_links(tag:insight_tags(*))`)
                .eq('id', newInsight.id)
                .single();
            if (updatedData) return mapRow(updatedData);
        }

        return newInsight;
    },

    async createBatch(inputs: InsightCreateInput[]): Promise<Insight[]> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const rows = inputs.map(input => ({
            user_id: user.id,
            conversation_id: input.conversationId,
            message_id: input.messageId || null,
            persona_id: input.personaId,
            context_id: input.contextId || null,
            collection_id: input.collectionId || null,
            source_type: input.sourceType,
            original_content: input.originalContent,
            highlighted_excerpt: input.highlightedExcerpt || null,
            ai_title: input.aiTitle,
            ai_summary: input.aiSummary,
            ai_category: input.aiCategory,
            ai_sentiment: input.aiSentiment,
            user_note: input.userNote || null,
        }));

        const { data, error } = await supabase.from('insights').insert(rows)
            .select(`*, tags:insight_tag_links(tag:insight_tags(*))`);
        if (error) throw error;

        const createdInsights = (data || []).map(mapRow);
        for (let i = 0; i < inputs.length; i++) {
            if (inputs[i].tagIds && inputs[i].tagIds!.length > 0 && createdInsights[i]) {
                await insightService.updateTags(createdInsights[i].id, inputs[i].tagIds!);
            }
        }

        return createdInsights;
    },

    async update(
        id: string,
        updates: Partial<Pick<Insight, 'aiTitle' | 'aiSummary' | 'aiCategory' | 'aiSentiment' | 'userNote' | 'isPinned' | 'collectionId'>>,
        tagIds?: string[]
    ): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbUpdates: Database['public']['Tables']['insights']['Update'] = {};
        if (updates.aiTitle !== undefined) dbUpdates.ai_title = updates.aiTitle;
        if (updates.aiSummary !== undefined) dbUpdates.ai_summary = updates.aiSummary;
        if (updates.aiCategory !== undefined) dbUpdates.ai_category = updates.aiCategory;
        if (updates.aiSentiment !== undefined) dbUpdates.ai_sentiment = updates.aiSentiment;
        if (updates.userNote !== undefined) dbUpdates.user_note = updates.userNote;
        if (updates.isPinned !== undefined) dbUpdates.is_pinned = updates.isPinned;
        if (updates.collectionId !== undefined) dbUpdates.collection_id = updates.collectionId;
        dbUpdates.updated_at = new Date().toISOString();

        if (Object.keys(dbUpdates).length > 1) {
            const { error } = await supabase.from('insights')
                .update(dbUpdates).eq('id', id).eq('user_id', user.id);
            if (error) throw error;
        }

        if (tagIds !== undefined) {
            await insightService.updateTags(id, tagIds);
        }
    },

    async updateTags(insightId: string, tagIds: string[]): Promise<void> {
        await supabase.from('insight_tag_links').delete().eq('insight_id', insightId);
        if (tagIds.length > 0) {
            const links = tagIds.map(tagId => ({ insight_id: insightId, tag_id: tagId }));
            await supabase.from('insight_tag_links').insert(links);
        }
    },

    async delete(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('insights').delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    async deleteBatch(ids: string[]): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');
        if (!ids.length) return;
        
        const { error } = await supabase.from('insights').delete().in('id', ids).eq('user_id', user.id);
        if (error) throw error;
    },

    async extractInsight(
        content: string,
        persona: { name: string; age: number | string; occupation: string; bio: string },
        context?: { name: string; description: string },
        categories?: { id: string; name: string }[]
    ) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Usuário não autenticado.');

        const { data, error } = await supabase.functions.invoke('gemini-insights', {
            body: { mode: 'extract', content, persona, context, categories },
            headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);
        return data as { title: string; summary: string; category: string; sentiment: string; excerpt: string };
    },

    async analyzeConversation(
        history: { role: string; content: string }[],
        persona: { name: string; age: number | string; occupation: string; bio: string },
        context?: { name: string; description: string },
        categories?: { id: string; name: string }[]
    ) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Usuário não autenticado.');

        const { data, error } = await supabase.functions.invoke('gemini-insights', {
            body: { mode: 'analyze', history, persona, context, categories },
            headers: { Authorization: `Bearer ${session.access_token}` },
        });

        if (error) throw new Error(error.message);
        if (data?.error) throw new Error(data.error);
        return data as { insights: { title: string; summary: string; category: string; sentiment: string; excerpt: string; messageContent: string }[] };
    },

    // -----------------------------------------------------------------------
    // Collections
    // -----------------------------------------------------------------------

    async fetchCollections(): Promise<InsightCollection[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase
            .from('insight_collections')
            .select('*')
            .eq('user_id', user.id)
            .order('name', { ascending: true });

        if (error) throw error;
        return (data || []).map(mapCollectionRow);
    },

    async createCollection(name: string, color: string, description?: string): Promise<InsightCollection> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase.from('insight_collections').insert({
            user_id: user.id,
            name,
            color,
            description: description || null,
        }).select().single();

        if (error) throw error;
        return mapCollectionRow(data);
    },

    async updateCollection(id: string, updates: Partial<Pick<InsightCollection, 'name' | 'color' | 'description'>>): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbUpdates: Database['public']['Tables']['insight_collections']['Update'] = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.color !== undefined) dbUpdates.color = updates.color;
        if (updates.description !== undefined) dbUpdates.description = updates.description;
        dbUpdates.updated_at = new Date().toISOString();

        if (Object.keys(dbUpdates).length <= 1) return;

        const { error } = await supabase.from('insight_collections')
            .update(dbUpdates).eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    async deleteCollection(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('insight_collections')
            .delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    // -----------------------------------------------------------------------
    // Tags
    // -----------------------------------------------------------------------

    async fetchTags(): Promise<InsightTag[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase
            .from('insight_tags')
            .select('*')
            .eq('user_id', user.id)
            .order('name', { ascending: true });

        if (error) throw error;
        return (data || []).map(mapTagRow);
    },

    async createTag(name: string, color: string): Promise<InsightTag> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase.from('insight_tags').insert({
            user_id: user.id,
            name,
            color,
        }).select().single();

        if (error) throw error;
        return mapTagRow(data);
    },

    async updateTag(id: string, updates: Partial<Pick<InsightTag, 'name' | 'color'>>): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbUpdates: Database['public']['Tables']['insight_tags']['Update'] = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.color !== undefined) dbUpdates.color = updates.color;

        if (Object.keys(dbUpdates).length === 0) return;

        const { error } = await supabase.from('insight_tags')
            .update(dbUpdates).eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    async deleteTag(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('insight_tags')
            .delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    // -----------------------------------------------------------------------
    // Categories
    // -----------------------------------------------------------------------

    async fetchCategories(): Promise<InsightCategory[]> {
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) return [];

        const { data, error } = await supabase
            .from('insight_categories')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: true });

        if (error) throw error;
        return (data || []).map(mapCategoryRow);
    },

    async createCategory(name: string, emoji: string, color: string): Promise<InsightCategory> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { data, error } = await supabase.from('insight_categories').insert({
            user_id: user.id,
            name,
            emoji,
            color,
        }).select().single();

        if (error) throw error;
        return mapCategoryRow(data);
    },

    async updateCategory(id: string, updates: Partial<Pick<InsightCategory, 'name' | 'emoji' | 'color'>>): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const dbUpdates: Database['public']['Tables']['insight_categories']['Update'] = {};
        if (updates.name !== undefined) dbUpdates.name = updates.name;
        if (updates.emoji !== undefined) dbUpdates.emoji = updates.emoji;
        if (updates.color !== undefined) dbUpdates.color = updates.color;
        dbUpdates.updated_at = new Date().toISOString();

        if (Object.keys(dbUpdates).length <= 1) return;

        const { error } = await supabase.from('insight_categories')
            .update(dbUpdates).eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },

    async deleteCategory(id: string): Promise<void> {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('User not authenticated');

        const { error } = await supabase.from('insight_categories')
            .delete().eq('id', id).eq('user_id', user.id);
        if (error) throw error;
    },
};
